import React, { useEffect, useRef, useState } from "react";

import {
	Button,
	DatePicker,
	Input,
	Popover,
	TextArea,
	TimePicker,
} from "@amphore-dev/amphore-lib";
import { InfoMessage } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { Form, Formik, FormikProps } from "formik";
import { t } from "i18next";
import * as Yup from "yup";

import SearchService from "@/services/SearchService";

import { useActiveView } from "@/hooks/useActiveView";

import { TaskOption } from "@/components/atoms";
import { ProjectSelect } from "@/components/molecules";

import { fieldError } from "@/utils/UForms";
import { computeSearchResults } from "@/utils/USearch";

import {
	TTaskEditFormValues,
	TTaskProject,
	TTaskSearchResultOptions,
} from "@/types";

/**
 * The task-name field: typing both writes straight through to Formik's
 * `name` (unlike SearchBar, which never persists a value — this field is
 * "free text that can optionally match a suggestion", not "pick one of
 * these options") and, debounced, searches for matching tasks to suggest
 * — picking one also fills in `SelectedTask` and defaults `project`. Not
 * SearchBar/AsyncSelect underneath: neither's contract fits a value that's
 * plain text with suggestions layered on top, rather than "the value IS
 * one of the options".
 */
const TaskNameField: React.FC<{
	value: string;
	onChange: (value: string) => void;
	onSelectTask: (task: TTaskSearchResultOptions) => void;
	label: string;
	placeholder: string;
	error?: string;
}> = ({ value, onChange, onSelectTask, label, placeholder, error }) => {
	const [suggestions, setSuggestions] = useState<TTaskSearchResultOptions[]>(
		[]
	);
	const [open, setOpen] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined
	);
	// Only the latest keystroke's answer may show: a slow one for an older
	// text (or one since cleared) would otherwise reopen stale suggestions.
	const latestSearch = useRef(0);

	// A search still waiting for its debounce goes with the field.
	useEffect(() => () => clearTimeout(debounceRef.current), []);

	const handleChange = (next: string) => {
		onChange(next);
		clearTimeout(debounceRef.current);
		const id = ++latestSearch.current;

		if (!next) {
			setSuggestions([]);
			setOpen(false);
			return;
		}

		debounceRef.current = setTimeout(() => {
			SearchService.search({
				keyword: next,
				include: ["tasks"],
			})
				.then((resp) => {
					if (id !== latestSearch.current) return;
					const tasks =
						computeSearchResults(resp, true).find(
							(group) => group.label === "Tasks"
						)?.options ?? [];
					setSuggestions(tasks as TTaskSearchResultOptions[]);
					setOpen(tasks.length > 0);
				})
				// Suggestions are a convenience: a failed lookup just
				// suggests nothing, typing goes on.
				.catch(() => undefined);
		}, 500);
	};

	// Always wrapped, even with nothing to suggest: swapping a bare Input for
	// a Popover-wrapped one once the first results came in remounted the
	// Input, and the field lost focus mid-typing. Popover opens on a click of
	// its trigger, so `open` is gated on there being suggestions — otherwise
	// clicking the (still empty) field would show an empty panel.
	const hasSuggestions = suggestions.length > 0;

	return (
		<Popover
			open={open && hasSuggestions}
			onOpenChange={(next) => setOpen(next && hasSuggestions)}
			closeOnClick
			matchTriggerWidth
			content={
				<div className="flex max-h-64 flex-col gap-1 overflow-auto">
					{suggestions.map((task) => (
						<button
							key={task.value}
							type="button"
							className="flex w-full items-center gap-2 rounded-sm px-2 py-1 text-left hover:bg-neutral-100"
							onClick={() => {
								onSelectTask(task);
								setOpen(false);
							}}
						>
							<TaskOption data={task} />
						</button>
					))}
				</div>
			}
		>
			<Input
				label={label}
				placeholder={placeholder}
				value={value}
				onChange={handleChange}
				error={error}
			/>
		</Popover>
	);
};

const initialValues: TTaskEditFormValues = {
	project: undefined,
	projectId: undefined,
	name: "",
	SelectedTask: undefined,
	workedOn: "",
	notes: "",
	startTime: format(new Date(), "HH:mm"),
	endTime: "",
	startDate: new Date(),
	endDate: null,
};

const validationSchema = () =>
	Yup.object().shape({
		project: Yup.object().required(t("edit.errors.project")),
		name: Yup.string().required(t("edit.errors.taskName")),
		startTime: Yup.string().required(t("edit.errors.startTime")),
		endDateCheck: Yup.string()
			.nullable()
			.test(
				"is-after-start-date",
				t("edit.errors.endDateBeforeStartDate"), // Replace with your error message
				function () {
					const { startDate, startTime, endDate, endTime } =
						this.parent; // Access startDate from the same object
					if (!startDate || !endDate) return true; // Skip validation if either is empty

					const startDateTime = new Date(
						format(startDate, "yyyy-MM-dd") + " " + startTime
					).getTime();
					const endDateTime = new Date(
						format(endDate, "yyyy-MM-dd") + " " + endTime
					).getTime();

					return endDateTime > startDateTime;
				}
			),
		WorkedOn: Yup.string(),
	});

export interface ITaskEditFormProps {
	onSubmit: (values: TTaskEditFormValues) => Promise<void>;
	initialValues?: TTaskEditFormValues;
	innerRef?: React.Ref<FormikProps<TTaskEditFormValues>>;
}

export const TaskEditForm: React.FC<ITaskEditFormProps> = ({
	onSubmit,
	innerRef,
	...props
}) => {
	const { view } = useActiveView();

	return (
		<Formik
			initialValues={{ ...initialValues, ...props.initialValues }}
			onSubmit={onSubmit}
			validationSchema={validationSchema()}
			innerRef={innerRef}
		>
			{(formik) => {
				const { values, setValues, setFieldValue, errors } = formik;
				return (
					<Form className="flex w-full flex-col justify-between">
						<div className="flex flex-col gap-4">
							<div>
								<TaskNameField
									label={t("tasks.name").toString()}
									placeholder={t("tasks.namePlaceholder")}
									error={fieldError(formik, "name")}
									value={values.name ?? ""}
									onChange={(value) =>
										setFieldValue("name", value)
									}
									onSelectTask={(task) => {
										setValues((prev) => ({
											...prev,
											name: task.name,
											SelectedTask: task,
											project:
												prev.project ?? task.project,
										}));
									}}
								/>
							</div>
							<div>
								<ProjectSelect
									label={t("projects.label").toString()}
									viewId={view?.id}
									hideArchived
									value={values.project}
									onChange={(value) =>
										setFieldValue(
											"project",
											value as TTaskProject | null
										)
									}
									placeholder={t(
										"projects.select"
									).toString()}
									error={fieldError(formik, "project")}
									className="z-2"
								/>
							</div>
							<div className="flex w-full items-center justify-between gap-4 [&>div]:w-full">
								<div className="[&>div]:flex">
									<DatePicker
										label={t("tasks.startDate").toString()}
										value={values.startDate}
										onChange={(date) => {
											setValues({
												...values,
												startDate: date || new Date(),
											});
										}}
										max={new Date()}
									/>
								</div>
								<TimePicker
									label={t("tasks.startTime").toString()}
									value={values.startTime}
									error={fieldError(formik, "startTime")}
									onChange={(value) =>
										setFieldValue("startTime", value ?? "")
									}
								/>
							</div>
							{values.endDate && (
								<div className="flex w-full items-center justify-between gap-4 [&>div]:w-full">
									<div className="[&>div]:flex">
										<DatePicker
											label={t("tasks.dateTo").toString()}
											value={values.endDate || null}
											onChange={(date) => {
												setValues({
													...values,
													endDate: date || null,
												});
											}}
											min={values.startDate}
											max={new Date()}
										/>
									</div>
									<TimePicker
										label={t("tasks.endTime").toString()}
										value={values.endTime || "00:00"}
										onChange={(value) =>
											setFieldValue(
												"endTime",
												value ?? "00:00"
											)
										}
									/>
								</div>
							)}
							{!!errors.endDateCheck && (
								<InfoMessage color="danger">
									{errors.endDateCheck}
								</InfoMessage>
							)}
							<Button
								onClick={() => {
									setValues({
										...values,
										endDate: !!values.endDate
											? null
											: new Date(),
										endTime: !!values.endDate
											? ""
											: format(new Date(), "HH:mm"),
									});
								}}
								variant="outline"
								size="sm"
								color={!values.endDate ? "danger" : "success"}
							>
								{!!values.endDate
									? t("tasks.resume")
									: t("tasks.end")}
							</Button>
							<div>
								<TextArea
									label={t("tasks.description").toString()}
									name="workedOn"
									className="h-full min-h-32"
									value={values.workedOn}
									onChange={(value) => {
										setFieldValue("workedOn", value);
									}}
									placeholder={t(
										"tasks.description"
									).toString()}
								/>
							</div>
						</div>
					</Form>
				);
			}}
		</Formik>
	);
};
